import type { Identity } from "./browser.ts";

import { isHttpProtocol, parseUrl } from "../url/url.ts";
import { currentIdentity } from "./browser.ts";
import { clientIdCookieScope, clientIdParam } from "./clientId.ts";
import { sessionParam } from "./session.ts";

// Target outside the cookie scope mints its own id → carry, else the visitor re-buckets.
function shouldCarryClientId(target: URL, from: URL): boolean {
  return (
    isHttpProtocol(target) &&
    clientIdCookieScope(target.host) !== clientIdCookieScope(from.host)
  );
}

// sessionStorage is per origin → any origin hop needs the session on the url.
function shouldCarrySession(target: URL, from: URL): boolean {
  return isHttpProtocol(target) && target.origin !== from.origin;
}

function applyIdentityToUrl(
  target: string,
  identity: Identity = currentIdentity(),
): string {
  const url = parseUrl(target);
  const from = parseUrl(identity.from);
  if (url === null || from === null) {
    return target;
  }

  const sessionUuid = shouldCarrySession(url, from)
    ? identity.sessionUuid
    : null;
  const clientId = shouldCarryClientId(url, from) ? identity.clientId : null;
  // Nothing carried → target verbatim, else a fragment-only href turns absolute.
  if (sessionUuid === null && clientId === null) {
    return target;
  }

  if (sessionUuid !== null) {
    url.searchParams.set(sessionParam, sessionUuid);
  }
  if (clientId !== null) {
    url.searchParams.set(clientIdParam, clientId);
  }

  return url.href;
}

// Incoming id already rejected or replaced → strip before it is carried over.
function withoutClientId(url: string): string {
  const parsed = parseUrl(url);
  if (parsed === null) {
    return url;
  }

  parsed.searchParams.delete(clientIdParam);
  return parsed.href;
}

export {
  applyIdentityToUrl,
  shouldCarryClientId,
  shouldCarrySession,
  withoutClientId,
};
